import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatPhoneNumber } from '@/utils/phoneUtils';
import { createStandardizedFormSubmission } from '@/utils/formSubmissionUtils';
import { useInterestDataSync } from '@/hooks/useInterestData';
import { updateCarfaxFormSubmitted } from '@/utils/carfaxTrackingUtils';
import { logger } from '@/utils/logger';

interface CarfaxFormProps {
  formData: { [key: string]: string };
  onUpdateData: (data: { [key: string]: string }) => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  onSubmit?: (formData: { [key: string]: string }) => void;
  vehicleVin?: string; // Add VIN prop to receive from vehicle card
  title?: string;
}

const CarfaxForm: React.FC<CarfaxFormProps> = ({
  formData,
  onUpdateData,
  onSubmit,
  vehicleVin, // Receive VIN from props
}) => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localFormData, setLocalFormData] = useState(formData);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const storedInterest = useInterestDataSync();
  
  // Uncontrolled input refs
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  
  // Tick to force re-render so the Send button's disabled state re-evaluates
  // against the latest ref values as the user types into either field.
  const [, setInputTick] = useState(0);

  // Sync local state when formData changes but stop once user starts typing
  useEffect(() => {
    if (!hasUserEdited) {
      setLocalFormData(formData);
    }
  }, [formData, hasUserEdited]);

  const handleInputChange = () => {
    if (!hasUserEdited) {
      setHasUserEdited(true);
    }
    setInputTick((t) => t + 1);
  };

  const handleFormSubmit = async () => {
    logger.log('🟠 CarfaxForm.handleFormSubmit invoked', { hasOnSubmit: !!onSubmit, isSubmitting });
    if (isSubmitting) return;
    
    
    setIsSubmitting(true);
    
    try {
      const name = nameInputRef.current?.value || '';
      const phone = phoneInputRef.current?.value || '';
      const interest = formData.interest || '';
      
      logger.log('=== CARFAX FORM SUBMISSION ===');
      logger.log('Stored interest data:', JSON.stringify(storedInterest, null, 2));

      // Use VIN from props (from vehicle card) or fall back to stored interest
      const vin = vehicleVin || storedInterest?.vehicle?.vin || '';
      logger.log('VIN from props (vehicle card):', vehicleVin);
      logger.log('VIN from stored interest:', storedInterest?.vehicle?.vin);
      logger.log('Final VIN to use:', vin);
      
      // Create Carfax-specific message
      const carfaxMessage = language === 'fr'
        ? `Demande de rapport Carfax - ${name} a demandé un rapport Carfax${vin ? ` pour NIV: ${vin}` : ''}. Contact: ${phone}`
        : `Carfax Report Request - ${name} has requested a Carfax report${vin ? ` for VIN: ${vin}` : ''}. Contact: ${phone}`;
      
      const carfaxInterestData = {
        ...storedInterest,
        message: carfaxMessage,
        department: storedInterest?.department || 'New'
      };

      const submissionData = createStandardizedFormSubmission(
        {
          name,
          phone,
          interest,
          vin
        },
        'carfax',
        formatPhoneNumber,
        carfaxInterestData
      );

      // Carfax form submitted
      
      // Track carfax form submission and construct URL if needed
      if (vin) {
        updateCarfaxFormSubmitted(vin);
        logger.log('Updated carfax form submitted timestamp for VIN:', vin);
        
        // Construct Carfax URL if not provided by AI
        const carfaxUrl = `https://www.carfax.com/VehicleHistory/p/Report.cfx?partner=CFL_0&vin=${vin}`;
        
        // Add constructed URL to submission data
        submissionData.carfaxUrl = carfaxUrl;
        logger.log('Constructed Carfax URL for VIN:', vin, 'URL:', carfaxUrl);
      }
      
      logger.log('🟠 CarfaxForm: about to invoke onSubmit', { hasOnSubmit: !!onSubmit, submissionData });
      if (onSubmit) {
        await onSubmit(submissionData);
        logger.log('🟠 CarfaxForm: onSubmit resolved');
      } else {
        console.error('💥 CarfaxForm: onSubmit prop is missing');
        toast.error('Form submission handler missing');
      }
      
      onUpdateData({ name, phone, interest });
    } catch (error) {
      console.error('💥 CARFAX_FORM submission threw:', error);
      toast.error(`Carfax submission failed: ${(error as Error)?.message || 'unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const name = nameInputRef.current?.value || '';
    const phone = phoneInputRef.current?.value || '';
    return phone.trim().length > 0 && name.trim().length > 0;
  };

  const phoneLabel = t('form.mobile');

  // Use VIN from props or fall back to stored interest for hidden field
  const currentVin = vehicleVin || storedInterest?.vehicle?.vin || '';

  return (
    <div className="p-2">
      <div className="mb-3">
        {/* Hidden Interest field */}
        <input type="hidden" name="interest" value={localFormData.interest || ''} />
        
        {/* Hidden VIN field */}
        <input type="hidden" name="vin" value={currentVin} />
        
        {/* Hidden topcarid field */}
        <input 
          id="topcarid" 
          name="topcarid" 
          type="hidden" 
          value={(() => {
            try {
              const topCarIdElement = document.getElementById('topcarid') as HTMLInputElement;
              return topCarIdElement?.value ? `Vehicle ID: ${topCarIdElement.value}` : '';
            } catch {
              return '';
            }
          })()}
        />
        
        {/* All fields on one row */}
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
             <Label htmlFor="name" className="text-xs text-gray-700">{t('form.name')}</Label>
             <Input
               id="name"
               name="name"
               type="text"
               autoComplete="name"
               defaultValue={localFormData.name || ''}
               ref={nameInputRef}
               onChange={handleInputChange}
               placeholder={t('form.name')}
               className="h-7 text-sm"
             />
           </div>
           <div className="w-36 space-y-1">
             <Label htmlFor="phone" className="text-xs text-gray-700">{phoneLabel}</Label>
             <Input
               id="phone"
               name="phone"
               type="tel"
               autoComplete="tel"
               defaultValue={localFormData.phone || ''}
               ref={phoneInputRef}
               onChange={handleInputChange}
               placeholder="555-555-1212"
               className="h-7 text-sm"
             />
           </div>
           <Button
              type="button"
              size="icon"
              onClick={handleFormSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="h-7 w-7 min-w-7 bg-red-600 hover:bg-red-700 flex-shrink-0"
              aria-label={isSubmitting ? t('form.submitting') : t('form.submit')}
            >
              <Send className="w-4 h-4" />
            </Button>
        </div>
      </div>
    </div>
  );
};

export default CarfaxForm;
